import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { isTenantAdmin } from "@/lib/tenants.functions";
import { isValidCoverImageUrl } from "@/lib/cover-image";
import { slugify, type SquadEvent } from "@/lib/events";

/**
 * Community event submissions.
 *
 * Anyone signed in can propose a night. A proposal is a real `events` row in the
 * `submitted` state: invisible to the public calendar, editable by the person who
 * sent it, and visible to crew in a review queue. Approving it flips the event to
 * a draft AND grants the submitter the host role — that grant is the only path,
 * and it is always a human decision.
 */

const emptyToNull = (val: unknown) =>
  val === null || val === undefined || (typeof val === "string" && val.trim() === "") ? null : val;

export const SITE_ORIGIN = "https://squadflight.com";

export const submissionSchema = z.object({
  title: z.string().trim().min(2, "Give your night a name.").max(120),
  starts_at: z.string().datetime(),
  ends_at: z.preprocess(emptyToNull, z.string().datetime().nullable().optional()),
  location: z.preprocess(emptyToNull, z.string().trim().max(255).nullable().optional()),
  capacity: z.coerce.number().int().min(1).max(500).default(8),
  spectator_capacity: z.coerce.number().int().min(0).max(500).optional(),
  description: z.preprocess(emptyToNull, z.string().trim().max(2000).nullable().optional()),
  cover_image_url: z.preprocess(
    emptyToNull,
    z
      .string()
      .trim()
      .max(1000)
      .refine(isValidCoverImageUrl, "Enter an image link or upload a photo")
      .nullable()
      .optional(),
  ),
});

export type SubmissionInput = z.infer<typeof submissionSchema>;

export type PendingSubmission = SquadEvent & {
  submitter_name: string;
  submitter_callsign: string | null;
  submitter_email: string | null;
  submitter_pilot_id: string | null;
  submitter_is_host: boolean;
};

const NO_PILOT_MESSAGE =
  "Finish setting up your pilot profile first — then come back and post your night.";

async function pilotForCaller(context: { supabase: any; userId?: string | null }) {
  const { data: tenantId } = await context.supabase.rpc("current_tenant_id");
  const { data: pilotId } = await context.supabase.rpc("pilot_id_for_user", {
    _user_id: context.userId,
  });
  if (!tenantId) throw new Error("Could not resolve tenant.");
  if (!pilotId) throw new Error(NO_PILOT_MESSAGE);
  return { tenantId: tenantId as string, pilotId: pilotId as string };
}

async function assertCrewOrAdmin(context: { supabase: any; userId?: string | null }) {
  if (await isTenantAdmin(context as never)) return;
  const { data: tenantId } = await context.supabase.rpc("current_tenant_id");
  const { data: crew } = await context.supabase.rpc("is_crew", {
    _user_id: context.userId,
    _tenant_id: tenantId,
  });
  if (!crew) throw new Error("Crew access required.");
}

const SUBMISSION_COLS =
  "id, title, slug, description, location, starts_at, ends_at, cover_image_url, status, campaign_id, partiful_url, featured, capacity, spectator_capacity, host_kind, host_pilot_id, freq_enabled, created_at, updated_at";

/** Is this person already a host? Used to skip the review queue entirely. */
export const mySubmissionContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: tenantId } = await context.supabase.rpc("current_tenant_id");
    const { data: pilotId } = await context.supabase.rpc("pilot_id_for_user", {
      _user_id: context.userId,
    });
    let isHost = false;
    if (tenantId) {
      const { data } = await context.supabase.rpc("is_host_pilot", {
        _user_id: context.userId,
        _tenant_id: tenantId,
      });
      isHost = !!data;
    }

    let pending: SquadEvent[] = [];
    if (pilotId) {
      const { data } = await context.supabase
        .from("events")
        .select(SUBMISSION_COLS)
        .eq("host_pilot_id", pilotId)
        .eq("status", "submitted")
        .order("starts_at", { ascending: true });
      pending = (data ?? []) as SquadEvent[];
    }

    return { isHost, hasPilot: !!pilotId, pending };
  });

export const submitCommunityEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => submissionSchema.parse(data))
  .handler(async ({ context, data }) => {
    const { tenantId, pilotId } = await pilotForCaller(context);

    // Gentle guard rail: a handful of open proposals is plenty for one pilot.
    const { count } = await context.supabase
      .from("events")
      .select("id", { count: "exact", head: true })
      .eq("host_pilot_id", pilotId)
      .eq("status", "submitted");
    if ((count ?? 0) >= 3) {
      throw new Error("You already have a few nights waiting on review — we'll get to them.");
    }

    const base = slugify(data.title) || "community-night";
    let event: any = null;
    let error: any = null;
    for (let attempt = 1; attempt <= 6; attempt++) {
      const slug = attempt === 1 ? base : `${base}-${attempt}`;
      const result = await context.supabase
        .from("events")
        .insert({
          tenant_id: tenantId,
          title: data.title,
          slug,
          description: data.description ?? null,
          location: data.location ?? null,
          starts_at: data.starts_at,
          ends_at: data.ends_at ?? null,
          capacity: data.capacity,
          spectator_capacity: data.spectator_capacity ?? 10,
          cover_image_url: data.cover_image_url ?? null,
          host_kind: "community",
          host_pilot_id: pilotId,
          created_by_user_id: context.userId,
          status: "submitted",
          featured: false,
          visibility: "public",
          freq_enabled: false,
          requires_freq_allocation: false,
          freq_escalation_hours: 24,
        } as never)
        .select(SUBMISSION_COLS)
        .single();
      event = result.data;
      error = result.error;
      if (!error) break;
      if (error.code !== "23505") break;
    }

    if (error || !event) {
      console.error("submitCommunityEvent failed", error);
      if (error?.code === "42501") {
        throw new Error("Your sign-in expired — sign in again and resend it.");
      }
      throw new Error("Could not send your event in. Give it another go.");
    }

    void notifyOfSubmission(context, event as SquadEvent, pilotId);
    return { event: event as SquadEvent };
  });

export const updateMySubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    submissionSchema.partial().extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const { id, ...rest } = data;
    const patch = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined));
    const { data: event, error } = await context.supabase
      .from("events")
      .update({ ...patch, updated_at: new Date().toISOString() } as never)
      .eq("id", id)
      .eq("status", "submitted")
      .select(SUBMISSION_COLS)
      .single();

    if (error || !event) {
      console.error("updateMySubmission failed", error);
      throw new Error("Could not save your changes.");
    }
    return { event: event as SquadEvent };
  });

export const listPendingSubmissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertCrewOrAdmin(context);
    const canManage = await isTenantAdmin(context as never);

    const { data, error } = await context.supabase
      .from("events")
      .select(
        `${SUBMISSION_COLS}, pilots!left(id, first_name, last_name, callsign, email, is_host)`,
      )
      .eq("status", "submitted")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("listPendingSubmissions failed", error);
      return { submissions: [] as PendingSubmission[], canManage };
    }

    const submissions = ((data ?? []) as any[]).map((row) => {
      const p = row.pilots ?? null;
      const { pilots: _drop, ...event } = row;
      return {
        ...event,
        submitter_name:
          [p?.first_name, p?.last_name].filter(Boolean).join(" ").trim() ||
          p?.callsign ||
          "Unknown",
        submitter_callsign: p?.callsign ?? null,
        submitter_email: p?.email ?? null,
        submitter_pilot_id: p?.id ?? null,
        submitter_is_host: !!p?.is_host,
      } as PendingSubmission;
    });

    return { submissions, canManage };
  });

export const approveSubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), publish: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    if (!(await isTenantAdmin(context as never))) throw new Error("Admin access required.");

    const { data: event, error } = await context.supabase
      .from("events")
      .update({
        status: data.publish ? "planned" : "draft",
        updated_at: new Date().toISOString(),
      } as never)
      .eq("id", data.id)
      .eq("status", "submitted")
      .select(SUBMISSION_COLS)
      .single();

    if (error || !event) {
      console.error("approveSubmission failed", error);
      throw new Error("Could not approve that submission.");
    }

    // Approving the night is what makes someone a host.
    let host: { email: string | null; first_name: string | null; was_host: boolean } | null = null;
    if (event.host_pilot_id) {
      const { data: pilot } = await context.supabase
        .from("pilots")
        .select("id, first_name, email, is_host")
        .eq("id", event.host_pilot_id)
        .maybeSingle();
      host = pilot
        ? { email: pilot.email, first_name: pilot.first_name, was_host: !!pilot.is_host }
        : null;
      if (pilot && !pilot.is_host) {
        const { error: hostError } = await context.supabase
          .from("pilots")
          .update({
            is_host: true,
            updated_at: new Date().toISOString(),
            updated_by_email: (context.claims?.email as string | undefined) ?? null,
          } as never)
          .eq("id", pilot.id);
        if (hostError) console.error("granting host on approval failed", hostError);
      }
    }

    if (host?.email) {
      try {
        const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
        await sendTemplateEmail("event-submission-approved", host.email, {
          templateData: {
            firstName: host.first_name ?? "",
            eventTitle: event.title,
            eventUrl: `${SITE_ORIGIN}/events/${event.slug}`,
            published: !!data.publish,
            newHost: !host.was_host,
          },
          idempotencyKey: `submission-approved-${event.id}`,
        });
      } catch (e) {
        console.error("approval email failed", e);
      }
    }

    return { event: event as SquadEvent };
  });

export const declineSubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), note: z.string().trim().max(500).optional() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    if (!(await isTenantAdmin(context as never))) throw new Error("Admin access required.");

    const { data: event, error } = await context.supabase
      .from("events")
      .update({ status: "cancelled", updated_at: new Date().toISOString() } as never)
      .eq("id", data.id)
      .eq("status", "submitted")
      .select(SUBMISSION_COLS)
      .single();

    if (error || !event) {
      console.error("declineSubmission failed", error);
      throw new Error("Could not decline that submission.");
    }

    if (event.host_pilot_id) {
      const { data: pilot } = await context.supabase
        .from("pilots")
        .select("first_name, email")
        .eq("id", event.host_pilot_id)
        .maybeSingle();
      if (pilot?.email) {
        try {
          const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
          await sendTemplateEmail("event-submission-declined", pilot.email, {
            templateData: {
              firstName: pilot.first_name ?? "",
              eventTitle: event.title,
              note: data.note ?? "",
            },
            idempotencyKey: `submission-declined-${event.id}`,
          });
        } catch (e) {
          console.error("decline email failed", e);
        }
      }
    }

    return { ok: true };
  });

/** Tell crew a night is waiting, and tell the submitter we've got it. Never blocks the submit. */
async function notifyOfSubmission(
  context: { supabase: any },
  event: SquadEvent,
  pilotId: string,
): Promise<void> {
  try {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const { getAdminEmails } = await import("@/lib/admin.functions");
    const { data: pilot } = await context.supabase
      .from("pilots")
      .select("first_name, last_name, callsign, email")
      .eq("id", pilotId)
      .maybeSingle();
    const name =
      [pilot?.first_name, pilot?.last_name].filter(Boolean).join(" ").trim() ||
      pilot?.callsign ||
      "A pilot";

    const admins = await getAdminEmails();
    for (const email of admins.emails) {
      await sendTemplateEmail("event-submission-received", email, {
        templateData: {
          submitterName: name,
          submitterEmail: pilot?.email ?? "",
          eventTitle: event.title,
          eventLocation: event.location ?? "",
          eventStartsAt: event.starts_at,
          capacity: event.capacity ?? 0,
          reviewUrl: `${SITE_ORIGIN}/events-manager`,
        },
        idempotencyKey: `submission-received-${event.id}-${email}`,
      });
    }

    if (pilot?.email) {
      await sendTemplateEmail("event-submission-confirmed", pilot.email, {
        templateData: {
          firstName: pilot.first_name ?? "",
          eventTitle: event.title,
          eventStartsAt: event.starts_at,
          editUrl: `${SITE_ORIGIN}/submit-event`,
        },
        idempotencyKey: `submission-confirmed-${event.id}`,
      });
    }
  } catch (e) {
    console.error("submission notifications failed", e);
  }
}
